"use client";

import {
  FC,
  LegacyRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import { parseCookies, setCookie } from "nookies";
import dayjs from "dayjs";
import useOnMounted from "apps/website/hooks/useOnMounted";
import useIntersectionObserver, {
  IOptions,
} from "libs/hooks/src/lib/useIntersectionObserver";
import { useExitIntent } from "apps/website/hooks/useExitIntent";
import { PublicConfig } from "apps/website/config/public";
import { useAuth } from "@auth/client-sdk-react";

import { IStoryBlokKlaviyoFormModal } from "../SectionKlaviyoForm.types";

declare global {
  interface Window {
    _klOnsite?: unknown[];
  }
}

const FORM_INTERSECTION_DEFAULTS: IOptions = {
  root: null,
  rootMargin: "0px",
  threshold: 0.5,
};

const SectionKlaviyoFormModal: FC<IStoryBlokKlaviyoFormModal> = (props) => {

  const { loggedIn } = useAuth();

  const COOKIE_MODAL_SEEN = `SectionKlaviyoFormModal-${props.formId}`;
  const [ modalWrapper, isVisible ] = useIntersectionObserver(FORM_INTERSECTION_DEFAULTS);
  const [ hasModalOpened, setHasModalOpened ] = useState(false);

  const canViewModal = () => {
    if (loggedIn) { return false; }
    if (typeof window === "undefined" || !!hasModalOpened) { return false; }
    if (parseCookies()[COOKIE_MODAL_SEEN]) { return false; }
    return true;
  };

  const triggerModal = useCallback(() => {
    if (!canViewModal()) { return; }

    setTimeout(() => {
      setHasModalOpened(true);
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push([ "openForm", props.formId ]);
    }, props.delay ?? 0);
  }, [ props.formId, hasModalOpened ]);

  useEffect(() => {
    const canSetCookie = !!props.daysUntilCanShowAgain && props.daysUntilCanShowAgain > 0;
    if (!canSetCookie || !hasModalOpened) { return; }

    setCookie(null, COOKIE_MODAL_SEEN, "true", {
      domain: PublicConfig.NEXT_PUBLIC_COOKIE_DOMAIN,
      expires: dayjs().add(props.daysUntilCanShowAgain ?? 1, "day").toDate(),
    });

  }, [ hasModalOpened ]);

  useOnMounted(() => {
    if (props.popupOn === "PAGE_LOAD") {
      triggerModal();
    }
  });

  useEffect(() => {
    if (!isVisible) { return; }
    if (props.popupOn === "SCROLL_DEPTH_HIT" && !hasModalOpened) {
      triggerModal();
    }
  }, [ isVisible ]);

  useExitIntent(() => {
    triggerModal();
  }, {
    disable: props.popupOn !== "EXIT_INTENT",
  });

  return (
    <div data-component={SectionKlaviyoFormModal.name} ref={ modalWrapper as LegacyRef<HTMLDivElement> } className="h-[1px] w-[1px] absolute">
      <div className="hidden">{ JSON.stringify(props) }</div>
    </div>
  );
};

export default SectionKlaviyoFormModal;
